import memoize from 'lodash/memoize'

import { createSelector } from '@app/utils/createSelector'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { ApiFeature } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'

const featuresSlice = createReduxSlice<{ hash: string; list: ApiFeature[] }>('features')

export const featuresResultSelector = createSelector([featuresSlice.selector, profileUserResultSelector], (slice, userResult) =>
  createLoadable(c => {
    const user = c.unwrap(userResult)
    if (!user) return []
    const hash = [user.id, user.account_type, user.account_type === 'sitter' ? user.has_training_request : 'null'].join(':')
    if (!slice || slice.hash !== hash)
      return c.load(
        createPromise('Features must be fetched', hash, async dispatch => {
          const { getFeatures } = await import('@app/packages/api/v2/actions/features')
          const response = await dispatch(callV2Api(getFeatures())).catch(catchFetchDataError(dispatch, 'Features fetch failed'))
          dispatch(featuresSlice.set({ hash, list: response.data }))
        })
      )
    return slice.list
  })
)

export const createFeatureEnabledSelector = memoize((name: string) =>
  mapThunkSelector(featuresResultSelector, features => !!features.find(f => f.attributes.name === name)?.attributes.enabled)
)

export const stripeEnabledSelector = createFeatureEnabledSelector('stripe')
export const savedAnnouncementsSearchEnabledSelector = createFeatureEnabledSelector('saved_announcements_searches')
export const onboardingV2EnabledResultSelector = createFeatureEnabledSelector('candidate_onboarding_v2')
